import { cn } from '@/lib/utils';

/**
 * Play Icon Component
 *
 * A customizable play button icon for media controls
 *
 * @example
 * ```tsx
 * <Play />
 * <Play className="text-blue-500" size={24} />
 * <Play fill="currentColor" width={18} height={18} />
 * <Play className="hover:text-pranik-brand-dark cursor-pointer" onClick={handlePlay} />
 * ```
 */

export interface PlayProps extends React.SVGProps<SVGSVGElement> {
  /**
   * Size of the icon (sets both width and height)
   * @default 18
   */
  size?: number;
  /**
   * Custom width (overrides size)
   */
  width?: number;
  /**
   * Custom height (overrides size)
   */
  height?: number;
  /**
   * Fill color for the icon
   * Use "currentColor" to inherit text color from parent
   * @default "currentColor"
   */
  fill?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Accessible label for screen readers
   */
  'aria-label'?: string;
}

export function Play({
  size = 18,
  width,
  height,
  fill = 'currentColor',
  className,
  'aria-label': ariaLabel,
  ...props
}: PlayProps) {
  const w = width ?? size;
  const h = height ?? size;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={w}
      height={h}
      viewBox="0 0 24 24"
      fill="none"
      className={cn('inline-block flex-shrink-0 transition-colors', className)}
      role={ariaLabel ? 'img' : 'presentation'}
      aria-label={ariaLabel}
      aria-hidden={!ariaLabel}
      {...props}
    >
      <path
        d="M20.494 7.96762L10.954 0.967621C10.2089 0.422006 9.32722 0.0934154 8.40677 0.0182844C7.48631 -0.0568467 6.56302 0.124418 5.7393 0.541978C4.91557 0.959539 4.22358 1.59708 3.74006 2.38391C3.25654 3.17073 3.00039 4.0761 3 4.99962V18.9996C3.00016 19.9236 3.25633 20.8294 3.74007 21.6166C4.22382 22.4037 4.91621 23.0415 5.74042 23.459C6.56463 23.8766 7.48841 24.0576 8.40925 23.982C9.33008 23.9063 10.212 23.577 10.957 23.0306L20.497 16.0306C21.1303 15.5662 21.6452 14.9592 22.0002 14.2587C22.3552 13.5582 22.5401 12.7839 22.5401 11.9986C22.5401 11.2133 22.3552 10.4391 22.0002 9.73856C21.6452 9.03806 21.1303 8.43103 20.497 7.96662L20.494 7.96762ZM19.31 14.4176L9.77 21.4176C9.32302 21.7442 8.79437 21.9408 8.24257 21.9856C7.69078 22.0304 7.13736 21.9216 6.64357 21.6712C6.14978 21.4209 5.73488 21.0389 5.4448 20.5674C5.15472 20.0958 5.00078 19.5532 5 18.9996V4.99962C4.99445 4.44497 5.1455 3.9 5.43578 3.42734C5.72606 2.95469 6.1438 2.57351 6.641 2.32762C7.06389 2.11251 7.53154 2.00014 8.006 1.99962C8.64189 2.00205 9.26022 2.20851 9.77 2.58862L19.31 9.58862C19.6895 9.86732 19.9981 10.2314 20.2107 10.6515C20.4234 11.0716 20.5343 11.5358 20.5343 12.0066C20.5343 12.4775 20.4234 12.9417 20.2107 13.3618C19.9981 13.7818 19.6895 14.1459 19.31 14.4246V14.4176Z"
        fill={fill}
      />
    </svg>
  );
}
